import type {
  BrandCompetitor,
  BrandPostMemory,
  CitationGap,
  CitationSummary,
  CompetitorBenchmarkResponse,
  GeoScoreResponse,
  VoiceMaturityResponse,
} from './blog';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

export const brandIntelApi = {
  updateOnboarding: (workspaceId: string, brandId: string, data: OnboardingProfile) =>
    api
      .patch(`/api/workspaces/${workspaceId}/brands/${brandId}/onboarding`, data)
      .then((r) => r.data),
  triggerCrawl: (workspaceId: string, brandId: string) =>
    api.post(`/api/workspaces/${workspaceId}/brands/${brandId}/crawl`).then((r) => r.data),
  getKnowledge: (workspaceId: string, brandId: string) =>
    api.get(`/api/workspaces/${workspaceId}/brands/${brandId}/knowledge`).then((r) => r.data),
  getPostMemory: (workspaceId: string, brandId: string) =>
    api
      .get<{
        rows: BrandPostMemory[];
        stats: Record<string, unknown>;
      }>(`/api/workspaces/${workspaceId}/brands/${brandId}/post-memory`)
      .then((r) => r.data),
  // Tier 3.9 — brand voice maturity widget. Aggregates post-memory +
  // citability into a 0–100 score. Visible to all plans; the response
  // motivates publishing rather than gating a feature.
  getVoiceMaturity: (workspaceId: string, brandId: string) =>
    api
      .get<VoiceMaturityResponse>(`/api/workspaces/${workspaceId}/brands/${brandId}/voice-maturity`)
      .then((r) => r.data),
  refreshPostMemory: (workspaceId: string, brandId: string) =>
    api
      .post(`/api/workspaces/${workspaceId}/brands/${brandId}/post-memory/refresh`)
      .then((r) => r.data),
  getGeoScore: (workspaceId: string, brandId: string, includeHistory = false) =>
    api
      .get<GeoScoreResponse>(`/api/workspaces/${workspaceId}/brands/${brandId}/geo-score`, {
        params: { includeHistory },
      })
      .then((r) => r.data),
  // WordPress connection
  connectWordPress: (
    workspaceId: string,
    brandId: string,
    data: { siteUrl: string; applicationUsername: string; applicationPassword: string },
  ) =>
    api
      .post(`/api/workspaces/${workspaceId}/brands/${brandId}/wordpress`, data)
      .then((r) => r.data),
  /**
   * V99 — connect via the Merkflow companion plugin instead of an
   * Application Password. Backend verifies via the plugin's
   * /wp-json/merkflow/v1/health endpoint before saving.
   */
  connectWordPressViaPlugin: (
    workspaceId: string,
    brandId: string,
    data: { siteUrl: string; token: string },
  ) =>
    api
      .post(`/api/workspaces/${workspaceId}/brands/${brandId}/wordpress/plugin`, data)
      .then((r) => r.data),
  /**
   * V99 — probe a (siteUrl, token) pair without saving. Used by the
   * "Test connection" button so a typo never lands as an active row.
   */
  testWordPressPlugin: (
    workspaceId: string,
    brandId: string,
    data: { siteUrl: string; token: string },
  ) =>
    api
      .post<{
        ok: boolean;
        message: string;
      }>(`/api/workspaces/${workspaceId}/brands/${brandId}/wordpress/plugin/test`, data)
      .then((r) => r.data),
  getWordPress: (workspaceId: string, brandId: string) =>
    api
      .get(`/api/workspaces/${workspaceId}/brands/${brandId}/wordpress`)
      .then((r) => r.data)
      .catch(() => null),
  disconnectWordPress: (workspaceId: string, brandId: string) =>
    api.delete(`/api/workspaces/${workspaceId}/brands/${brandId}/wordpress`).then((r) => r.data),
  publishLlmsTxt: (workspaceId: string, brandId: string) =>
    api
      .post<{
        publicUrl: string;
        bytes: number;
      }>(`/api/workspaces/${workspaceId}/brands/${brandId}/llms-txt`)
      .then((r) => r.data),
  // Custom probes
  getCustomProbes: (workspaceId: string, brandId: string) =>
    api
      .get<
        { id: string; brandId: string; queryText: string; active: boolean }[]
      >(`/api/workspaces/${workspaceId}/brands/${brandId}/probes`)
      .then((r) => r.data),
  addCustomProbe: (workspaceId: string, brandId: string, queryText: string) =>
    api
      .post(`/api/workspaces/${workspaceId}/brands/${brandId}/probes`, { queryText })
      .then((r) => r.data),
  deleteCustomProbe: (workspaceId: string, brandId: string, probeId: string) =>
    api
      .delete(`/api/workspaces/${workspaceId}/brands/${brandId}/probes/${probeId}`)
      .then((r) => r.data),
  runCustomProbe: (workspaceId: string, brandId: string, probeId: string) =>
    api
      .post<{
        engine: string;
        queryType: string;
        queryText: string;
        brandMentioned: boolean;
        mentionType: string;
        mentionContext: string | null;
        responseText: string | null;
        probedAt: string | null;
      }>(`/api/workspaces/${workspaceId}/brands/${brandId}/probes/${probeId}/run`)
      .then((r) => r.data),
  // Default probes (the 3 auto-generated direct/category/problem queries)
  getDefaultProbes: (workspaceId: string, brandId: string) =>
    api
      .get<
        {
          id: string;
          brandId: string;
          queryType: string;
          queryText: string;
          active: boolean;
          generatedAt: string | null;
        }[]
      >(`/api/workspaces/${workspaceId}/brands/${brandId}/probes/defaults`)
      .then((r) => r.data),
  regenerateDefaultProbes: (workspaceId: string, brandId: string) =>
    api
      .post<
        {
          id: string;
          brandId: string;
          queryType: string;
          queryText: string;
          active: boolean;
          generatedAt: string | null;
        }[]
      >(`/api/workspaces/${workspaceId}/brands/${brandId}/probes/defaults/regenerate`)
      .then((r) => r.data),
  toggleDefaultProbe: (workspaceId: string, brandId: string, probeId: string, active: boolean) =>
    api
      .patch(`/api/workspaces/${workspaceId}/brands/${brandId}/probes/defaults/${probeId}/active`, {
        active,
      })
      .then((r) => r.data),
  // Per-brand probe settings
  getProbeSettings: (workspaceId: string, brandId: string) =>
    api
      .get<{
        countryCode: string | null;
        languageCode: string | null;
        recency: string | null;
        inferredCountry: string | null;
        inferredLanguage: string | null;
      }>(`/api/workspaces/${workspaceId}/brands/${brandId}/probe-settings`)
      .then((r) => r.data),
  updateProbeSettings: (
    workspaceId: string,
    brandId: string,
    data: { countryCode?: string | null; languageCode?: string | null; recency?: string | null },
  ) =>
    api
      .put(`/api/workspaces/${workspaceId}/brands/${brandId}/probe-settings`, data)
      .then((r) => r.data),
  // Phase 7d — edit the queryText of an existing CUSTOM probe. Defaults
  // (is_custom=false) return 409; regenerate them instead via
  // regenerateDefaultProbes.
  patchCustomProbe: (workspaceId: string, brandId: string, probeId: string, queryText: string) =>
    api
      .patch<{
        id: string;
        brandId: string;
        queryText: string;
        active: boolean;
      }>(`/api/workspaces/${workspaceId}/brands/${brandId}/probes/${probeId}`, { queryText })
      .then((r) => r.data),
  // Trigger a full probe run (default + custom + Perplexity) in the
  // BACKGROUND. Backend returns 202 immediately and runs the probe on a
  // detached scheduler — closing the tab / proxy timeout no longer
  // cancels the chain. Frontend polls the geo-score endpoint for fresh
  // results. 429 if another run fired in the last hour.
  runAdhocProbe: (workspaceId: string, brandId: string) =>
    api
      .post<{
        status: string;
        startedAt: string;
        brandId: string;
      }>(`/api/workspaces/${workspaceId}/brands/${brandId}/geo-probe/run`)
      .then((r) => r.data),
  // Phase 7d — content-type breakdown for the gap analysis section.
  getContentTypeBreakdown: (workspaceId: string, brandId: string, windowDays: number = 30) =>
    api
      .get<
        ContentTypeBreakdownRow[]
      >(`/api/workspaces/${workspaceId}/brands/${brandId}/content-type-breakdown`, { params: { windowDays } })
      .then((r) => r.data),
  // Phase 7d — ranked recommendation list, server-cached 1h.
  getAiVisibilityRecommendations: (workspaceId: string, brandId: string) =>
    api
      .get<AiVisibilityRecommendationsResponse>(
        `/api/workspaces/${workspaceId}/brands/${brandId}/ai-visibility/recommendations`,
      )
      .then((r) => r.data),
  // V143 — brand URLs cited by Perplexity + Gemini, grouped by URL.
  getCitations: (workspaceId: string, brandId: string, windowDays: number = 30) =>
    api
      .get<CitationSummary[]>(`/api/workspaces/${workspaceId}/brands/${brandId}/citations`, {
        params: { windowDays },
      })
      .then((r) => r.data),
  // V143 — queries where competitors are cited but brand isn't.
  getCitationGaps: (workspaceId: string, brandId: string, windowDays: number = 30) =>
    api
      .get<CitationGap[]>(`/api/workspaces/${workspaceId}/brands/${brandId}/citation-gaps`, {
        params: { windowDays },
      })
      .then((r) => r.data),
  // V143 — owner-gated generate-blog-from-gap. Returns the draft.
  generateBlogFromGap: (workspaceId: string, brandId: string, queryText: string) =>
    api
      .post(`/api/workspaces/${workspaceId}/brands/${brandId}/citation-gaps/generate-blog`, {
        queryText,
      })
      .then((r) => r.data),
  // Phase 7d — 5/day regenerate counter for the default probes.
  // `adminBypass: true` when the user is a platform admin — counter is
  // still incremented but the cap isn't enforced server-side.
  getRegenerateDefaultsStatus: (workspaceId: string, brandId: string) =>
    api
      .get<{
        countToday: number;
        limit: number;
        canRegenerate: boolean;
        adminBypass: boolean;
      }>(`/api/workspaces/${workspaceId}/brands/${brandId}/probes/defaults/status`)
      .then((r) => r.data),
  // Phase 7d — PDF export (Agency only). Creates an async report_jobs
  // row and kicks off Puppeteer render in the background. Poll via
  // getReportJob until status === 'ready', then download via
  // downloadReportUrl.
  createAiVisibilityReport: (workspaceId: string, brandId: string) =>
    api
      .post<ReportJob>(`/api/workspaces/${workspaceId}/brands/${brandId}/ai-visibility/report`)
      .then((r) => r.data),
  getReportJob: (workspaceId: string, reportId: string) =>
    api.get<ReportJob>(`/api/workspaces/${workspaceId}/reports/${reportId}`).then((r) => r.data),
  downloadReportUrl: (workspaceId: string, reportId: string) =>
    `/api/workspaces/${workspaceId}/reports/${reportId}/download`,
  // Phase 7e Step 3 — competitor benchmarking. Pro+ gate; controller
  // returns 403 with feature_not_available body on Starter.
  listCompetitors: (workspaceId: string, brandId: string) =>
    api
      .get<BrandCompetitor[]>(`/api/workspaces/${workspaceId}/brands/${brandId}/competitors`)
      .then((r) => r.data),
  addCompetitor: (workspaceId: string, brandId: string, name: string, url?: string) =>
    api
      .post<BrandCompetitor>(`/api/workspaces/${workspaceId}/brands/${brandId}/competitors`, {
        name,
        url: url || null,
      })
      .then((r) => r.data),
  removeCompetitor: (workspaceId: string, brandId: string, competitorId: string) =>
    api
      .delete(`/api/workspaces/${workspaceId}/brands/${brandId}/competitors/${competitorId}`)
      .then((r) => r.data),
  // V132 — per-plan competitor cap. Returns { maxActive } where the int
  // varies by plan tier (trial=3, pro=5, agency=10). Frontend renders
  // the counter via this rather than hardcoding 3.
  getCompetitorLimit: (workspaceId: string, brandId: string) =>
    api
      .get<{
        maxActive: number;
      }>(`/api/workspaces/${workspaceId}/brands/${brandId}/competitors/limit`)
      .then((r) => r.data),
  getCompetitorBenchmark: (workspaceId: string, brandId: string, windowDays: number = 30) =>
    api
      .get<CompetitorBenchmarkResponse>(
        `/api/workspaces/${workspaceId}/brands/${brandId}/competitor-probes`,
        { params: { windowDays } },
      )
      .then((r) => r.data),
  // Phase 7f Step 2 — weekly correlation between publishing activity
  // and GEO score changes + 5-rule deterministic insights.
  getGeoCorrelation: (workspaceId: string, brandId: string, windowDays: number = 90) =>
    api
      .get<GeoCorrelationResponse>(
        `/api/workspaces/${workspaceId}/brands/${brandId}/geo-correlation`,
        { params: { windowDays } },
      )
      .then((r) => r.data),
  // Phase 7f Step 4 — pure-string brand naming consistency.
  getNamingConsistency: (workspaceId: string, brandId: string, windowDays: number = 90) =>
    api
      .get<NamingConsistencyResponse>(
        `/api/workspaces/${workspaceId}/brands/${brandId}/naming-consistency`,
        { params: { windowDays } },
      )
      .then((r) => r.data),
  // Phase 7f Step 5 — keyword co-occurrence buckets.
  getKeywordAnalysis: (workspaceId: string, brandId: string, windowDays: number = 90) =>
    api
      .get<KeywordAnalysisResponse>(
        `/api/workspaces/${workspaceId}/brands/${brandId}/keyword-analysis`,
        { params: { windowDays } },
      )
      .then((r) => r.data),
  // Phase 7f Step 3 — generate a post that addresses a specific probe miss.
  // The backend builds the gap-fix prompt and stamps probe_gap_result_id
  // on the resulting draft.
  generateGapFixPost: (workspaceId: string, brandId: string, resultId: string, platform?: string) =>
    api
      .post<GeneratePostsResponse>(
        `/api/workspaces/${workspaceId}/brands/${brandId}/probes/results/${resultId}/generate-post`,
        platform ? { platform } : {},
      )
      .then((r) => r.data),
  /** Run a single custom probe ad-hoc (Phase 7f follow-up — matches the
   *  per-row Run-now button on the Analytics-tab CustomProbesSection). */
  runCustomProbeNow: (workspaceId: string, brandId: string, probeId: string) =>
    api
      .post(`/api/workspaces/${workspaceId}/brands/${brandId}/probes/${probeId}/run`)
      .then((r) => r.data),
};

export interface TrendDiscoveryJobSummary {
  id: string;
  /** running | completed | failed | idle */
  status: 'running' | 'completed' | 'failed' | 'idle';
  startedAt: string;
  finishedAt: string | null;
  brandsProcessed: number;
  errorMessage: string | null;
}

export interface GeoCorrelationResponse {
  weeks: GeoCorrelationWeek[];
  insights: GeoCorrelationInsight[];
}
export interface GeoCorrelationWeek {
  weekStart: string;
  postsPublished: number;
  platformsActive: number;
  contentTypes: string[];
  avgCitabilityScore: number | null;
  geoScoreEnd: number | null;
  geoScoreDelta: number | null;
  probesMentioned: number;
  probesTotal: number;
}
export interface GeoCorrelationInsight {
  type: string;
  params: Record<string, string>;
}

export interface NamingConsistencyResponse {
  canonicalName: string;
  totalPosts: number;
  exactMatch: number;
  caseVariations: number;
  otherVariations: { text: string; count: number }[];
  notMentioned: number;
  consistencyPct: number;
}

export interface KeywordAnalysisResponse {
  totalScoredPosts: number;
  minPostsRequired: number;
  reinforced: {
    keyword: string;
    postCount: number;
    probesMentioned: number;
    probesTotal: number;
  }[];
  gaps: { keyword: string; postCount: number; probesMentioned: number; probesTotal: number }[];
  untested: { keyword: string; postCount: number }[];
  topPostKeywords: { keyword: string; count: number }[];
}

export interface ReportJob {
  id: string;
  workspaceId: string;
  brandId: string | null;
  periodStart: string;
  periodEnd: string;
  status: 'generating' | 'ready' | 'failed' | string;
  createdAt: string | null;
  completedAt: string | null;
  reportType: 'optimizer' | 'ai_visibility' | string;
}

// Phase 7d types ──────────────────────────────────────────────────────────

export interface AiVisibilityRecommendation {
  id: string;
  severity: 'HIGH' | 'MEDIUM' | 'LOW';
  /** English fallback — backend keeps populating these so admin / API
   *  consumers + a missing locale key still render readable copy. */
  title: string;
  body: string;
  actionLabel: string | null;
  actionHref: string | null;
  /** i18n keys (post-amendment-A). Frontend prefers these over the raw
   *  English fields. Interpolation values for body live in `interpolation`. */
  titleKey: string;
  bodyKey: string;
  actionKey: string | null;
  interpolation: Record<string, string | number>;
}

export interface AiVisibilityRecommendationsResponse {
  recommendations: AiVisibilityRecommendation[];
  generatedAt: string;
}

export interface ContentTypeBreakdownRow {
  contentType: string;
  count: number;
  recommendedMin: number;
}

export interface GeoProbeSummary {
  totalProbes: number;
  positiveMentions: number;
  negativeMentions: number;
  notFound: number;
  mentionRate: number;
  byEngine: {
    engine: string;
    probes: number;
    mentions: number;
    negativeMentions: number;
    latestMention: string | null;
  }[];
  results: {
    engine: string;
    queryType: string;
    queryText: string;
    brandMentioned: boolean;
    mentionType: string;
    mentionContext: string | null;
    responseText: string | null;
    probedAt: string | null;
  }[];
}

export interface BlogPostItem {
  id: string;
  brandId: string;
  workspaceId: string;
  title: string;
  slug: string | null;
  focusKeyword: string | null;
  metaDescription: string | null;
  content: string;
  excerpt: string | null;
  status: string;
  reviewStatus: string;
  wordCount: number | null;
  wpPostId: number | null;
  wpPostUrl: string | null;
  publishedAt: string | null;
  createdAt: string | null;
  // V80
  coverImageS3Key: string | null;
  excerptOverride: string | null;
  /** Signed public URL for the cover image, derived server-side from
   *  coverImageS3Key at read time. Null when no cover yet. Never pass
   *  back to the backend — it's a view-only field. */
  coverImageUrl: string | null;
}
